/* =====================================================================================
   The market selector.

   A market is a lens, not a folder. The same nephrology centre matters for IgA nephropathy AND
   for membranous nephropathy with a different patient population, a different competitive set
   and a different reason to call, so the centre is one record and what we know about it is
   answered per market.

   IT DISAPPEARS WHEN THERE IS ONLY ONE. Scira sells into a single market, and a dropdown with
   one option is a control that teaches the user nothing and costs them a glance every time they
   open the page. The bar renders only when a choice actually exists, which for most tenants is
   never.

   THE CHOICE IS REMEMBERED, IN TWO PLACES. localStorage so the current tab is instant, and
   app.members.default_market_id so a new laptop does not start blank. A rep who covers one
   market should never pick it twice.

   MARKET IS A FILTER, NOT A PERMISSION. Narrowing to a market changes what you are looking at.
   It never changes what you are allowed to see: that is org_id and row-level security, and
   nothing in this component touches either.
   ===================================================================================== */

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { BACKEND } from '../lib/db';
import './marketbar.css';

export interface Market {
  id: string; code: string; name: string; description: string | null; is_default: boolean;
}

interface Ctx {
  markets: Market[];
  marketId: string | null;      // null means every market
  setMarketId: (id: string | null) => void;
  ready: boolean;
}

const MarketCtx = createContext<Ctx>({
  markets: [], marketId: null, setMarketId: () => {}, ready: false,
});

export const useMarket = () => useContext(MarketCtx);

const KEY = 'forum.market';

export function MarketProvider({ children }: { children: React.ReactNode }) {
  const [markets, setMarkets] = useState<Market[]>([]);
  const [marketId, setId] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      if (BACKEND !== 'supabase') { if (live) setReady(true); return; }
      try {
        const { supabase } = await import('../lib/supabase');
        const { data } = await supabase().schema('atlas').rpc('list_markets');
        const rows = (data ?? []) as unknown as Market[];
        if (!live) return;
        setMarkets(rows);
        // Saved choice, then the member's stored default, then everything. Never a silent
        // narrowing to whichever market happened to be created first.
        const saved = localStorage.getItem(KEY);
        const valid = saved && rows.some((m) => m.id === saved) ? saved : null;
        setId(valid ?? rows.find((m) => m.is_default)?.id ?? null);
      } catch {
        /* No markets configured is a normal state, not an error: a tenant that has not bought
           market provisioning has none, and everything they hold carries a null market_id and
           is therefore visible under "every market". */
      } finally { if (live) setReady(true); }
    })();
    return () => { live = false; };
  }, []);

  const setMarketId = useCallback((id: string | null) => {
    setId(id);
    if (id) localStorage.setItem(KEY, id); else localStorage.removeItem(KEY);
    if (BACKEND === 'supabase') {
      import('../lib/supabase').then(({ supabase }) =>
        supabase().schema('atlas').rpc('set_default_market', { p_market: id })).catch(() => {
          /* Remembering the choice on the server is a convenience. If it fails the tab still
             works from localStorage, and there is nothing useful to tell the user. */
        });
    }
  }, []);

  return (
    <MarketCtx.Provider value={{ markets, marketId, setMarketId, ready }}>
      {children}
    </MarketCtx.Provider>
  );
}

/* The visible control. Renders nothing at all below two markets. */
export function MarketBar() {
  const { markets, marketId, setMarketId } = useMarket();
  if (markets.length < 2) return null;

  const current = markets.find((m) => m.id === marketId);

  return (
    <div className="mkt-bar">
      <span className="mkt-label">Market</span>
      <div className="mkt-choices" role="radiogroup" aria-label="Market">
        <button role="radio" aria-checked={marketId === null}
                className={marketId === null ? 'on' : ''}
                onClick={() => setMarketId(null)}
                title="Everything, across every market">
          All
        </button>
        {markets.map((m) => (
          <button key={m.id} role="radio" aria-checked={marketId === m.id}
                  className={marketId === m.id ? 'on' : ''}
                  onClick={() => setMarketId(m.id)}
                  title={m.description ?? m.name}>
            {m.name}
          </button>
        ))}
      </div>
      {current?.description && <p className="mkt-desc">{current.description}</p>}
    </div>
  );
}
